import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import styles from './RoleFormDialog.module.css';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { ApiError } from '../../lib/api/client';
import { rolesApi, type Permission, type Role } from '../../lib/api/users';

/**
 * Create a role, or edit one.
 *
 * ## Two calls on an edit, and why they are not one
 *
 * The labels go to `PATCH /roles/{role}`; the permissions go to
 * `PUT /roles/{role}/permissions`. That mirrors the API, which separates them
 * because they carry different audit actions — renaming a label and changing
 * what a role may DO are read by different people for different reasons, and
 * one entry covering both buries the half that matters.
 *
 * Only the calls that are needed are made: editing a label alone sends one
 * request, not two.
 *
 * ## `name` is only editable on create
 *
 * It is the join key the permission bypass, the legacy role mapper and the
 * seeder all resolve roles by, so the server refuses to change it. The field
 * is shown read-only on an edit rather than hidden — an administrator looking
 * for it should see it is fixed, not wonder where it went.
 *
 * ## Reserved permissions render disabled, never absent
 *
 * With the reason beside them. See `RoleScreen`'s own docblock.
 */

export interface RoleFormDialogProps {
  open: boolean;
  /** Undefined to create. */
  role?: Role;
  catalogue: readonly Permission[];
  onClose: () => void;
  onSaved: (role: Role) => void;
}

/** `case.view` -> `case`. The heading a group of checkboxes sits under. */
function groupOf(name: string): string {
  const dot = name.indexOf('.');

  return dot === -1 ? name : name.slice(0, dot);
}

export function RoleFormDialog({ open, role, catalogue, onClose, onSaved }: RoleFormDialogProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [labelAr, setLabelAr] = useState('');
  const [granted, setGranted] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // The dialog is reused across opens rather than remounted, so every field is
  // reset explicitly, keyed on the row it is about to show.
  useEffect(() => {
    if (!open) return;

    setName(role?.name ?? '');
    setLabelEn(role?.label_en ?? '');
    setLabelAr(role?.label_ar ?? '');
    setGranted(role?.permissions ?? []);
    setFailure(null);
  }, [open, role]);

  const groups = [...new Set(catalogue.map((permission) => groupOf(permission.name)))].sort();

  function toggle(permission: string): void {
    setGranted((current) =>
      current.includes(permission)
        ? current.filter((one) => one !== permission)
        : [...current, permission],
    );
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();

    if (saving) return;

    setSaving(true);
    setFailure(null);

    try {
      let saved: Role;

      if (role === undefined) {
        saved = await rolesApi.create({
          name: name.trim(),
          label_en: labelEn.trim(),
          label_ar: labelAr.trim(),
          permissions: granted,
        });
      } else {
        /*
         * Only what changed. `PATCH` with an unchanged label is a no-op the
         * server already handles, but a `PUT /permissions` that changes
         * nothing would still MARK THE ROLE CUSTOMISED and take it out of the
         * seeder's hands — a side effect nobody asked for by opening a dialog
         * and pressing Save.
         */
        const labelsChanged = labelEn.trim() !== role.label_en || labelAr.trim() !== role.label_ar;
        const permissionsChanged =
          [...granted].sort().join(',') !== [...(role.permissions ?? [])].sort().join(',');

        saved = labelsChanged
          ? await rolesApi.update(role.uuid, { label_en: labelEn.trim(), label_ar: labelAr.trim() })
          : role;

        if (permissionsChanged) {
          saved = await rolesApi.setPermissions(role.uuid, granted);
        }
      }

      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // The server's own message: it names the reserved permission, or the
      // holder count, and a generic failure would discard the only part that
      // tells the administrator what to do next.
      setFailure(error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      /*
       * `form`, and `lg`. The title names a task ("New role") rather than
       * asking a question, which is what `kind` selects; and the permission
       * matrix is a 37-row checklist, which at the default 420px wraps every
       * `case.view` onto two lines and puts its description under it.
       */
      kind="form"
      size="lg"
      title={role === undefined ? t('roles.createTitle') : t('roles.editTitle', { role: role.name })}
      onClose={onClose}
      actions={
        <>
          <Button
            icon={<FloppyDiskIcon size={16} />}
            loading={saving}
            onClick={(event) => {
              void submit(event);
            }}
          >
            {t('roles.save')}
          </Button>
          <Button icon={<XIcon size={16} />} variant="ghost" onClick={onClose}>
            {t('roles.cancel')}
          </Button>
        </>
      }
    >
      <form className={styles['form']} onSubmit={(event) => void submit(event)}>
        <div className={styles['identity']}>
          <Input
            label={t('roles.fieldName')}
            width="code"
            value={name}
            /* Fixed after creation — the server refuses a rename. Shown
               rather than hidden so it is visibly fixed. */
            readOnly={role !== undefined}
            hint={role === undefined ? t('roles.nameHint') : t('roles.nameFixed')}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <Input
            label={t('roles.fieldLabelEn')}
            width="fill"
            value={labelEn}
            onChange={(event) => {
              setLabelEn(event.target.value);
            }}
          />
          <Input
            label={t('roles.fieldLabelAr')}
            width="fill"
            value={labelAr}
            onChange={(event) => {
              setLabelAr(event.target.value);
            }}
          />
        </div>

        <fieldset className={styles['permissions']}>
          <legend className={styles['legend']}>{t('roles.permissionsLegend')}</legend>

          {groups.map((group) => (
            <div key={group} className={styles['group']}>
              <h4 className={styles['groupTitle']}>{group}</h4>

              {catalogue
                .filter((permission) => groupOf(permission.name) === group)
                .map((permission) => (
                  <label key={permission.name} className={styles['permission']}>
                    <input
                      type="checkbox"
                      checked={granted.includes(permission.name)}
                      disabled={permission.reserved}
                      onChange={() => {
                        toggle(permission.name);
                      }}
                    />
                    <span className={styles['permissionName']}>{permission.name}</span>
                    <span className={styles['permissionWhy']}>
                      {permission.reserved
                        ? t('roles.reserved')
                        : (permission.description ?? '')}
                    </span>
                  </label>
                ))}
            </div>
          ))}
        </fieldset>

        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>
      </form>
    </Dialog>
  );
}
